// Pure helpers behind the Experiments v2 results view: metric catalogue,
// number formats, health checks, the recommendation line and the
// players-over-time series. The layout follows the common A/B tools
// (Firebase A/B Testing, Statsig, GrowthBook): health first, then a scorecard
// grouped primary / guardrails / secondary.
import type {
  ExpV2Comparison,
  ExpV2Counts,
  ExpV2Detail,
  ExpV2MetricResult,
  ExpV2MetricUnit,
  ExpV2Results,
  ExpV2Test,
  ExpV2Timeline,
  ExpV2Verdict,
} from '@/types/experimentsV2';

/** The metrics the server scores (backend admin-experiments-v2.metrics.ts). Same keys. */
export const METRIC_CATALOGUE: { key: string; label: string }[] = [
  { key: 'uninstall_24h', label: 'Uninstalled within 24 h' },
  { key: 'd1_retention', label: 'D1 retention' },
  { key: 'd3_retention', label: 'D3 retention' },
  { key: 'd7_retention', label: 'D7 retention' },
  { key: 'matches_started_24h', label: 'Matches started per player (24 h)' },
  { key: 'matches_completed_24h', label: 'Matches completed per player (24 h)' },
  { key: 'session_minutes_24h', label: 'Minutes played per player (24 h)' },
  { key: 'payer_7d', label: 'Paid within 7 days' },
  { key: 'revenue_inr_7d', label: 'IAP revenue per player (7 days, INR)' },
];

export const isCatalogueMetric = (key?: string | null): boolean =>
  !!key && METRIC_CATALOGUE.some((m) => m.key === key);

export const metricLabel = (key: string): string =>
  METRIC_CATALOGUE.find((m) => m.key === key)?.label ?? key;

/** Run a test at least this long from the stable phase: one full weekly cycle. */
export const MIN_RUN_DAYS = 7;
const DAY_MS = 86_400_000;

/**
 * Variant colours in fixed order (control first), categorical slots 1-5 of
 * the validated palette. Below 3:1 on white for slots 3-5, so every chart that
 * uses them also has a legend and a table with the same numbers.
 */
export const VARIANT_COLORS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4'];

export function variantOrder(t: Pick<ExpV2Test, 'variants'>): string[] {
  const keys = t.variants.map((v) => v.key);
  return keys.includes('control') ? ['control', ...keys.filter((k) => k !== 'control')] : keys;
}

export const variantColor = (order: string[], variant: string): string =>
  VARIANT_COLORS[order.indexOf(variant)] ?? '#8c8c8c';

/** Status colours, always shown with an icon and a word, never alone. */
export const VERDICT_STYLE: Record<
  ExpV2Verdict,
  { color: string; label: string; tag: 'success' | 'error' | 'default' }
> = {
  better: { color: '#0ca30c', label: 'Better', tag: 'success' },
  worse: { color: '#d03b3b', label: 'Worse', tag: 'error' },
  neutral: { color: '#8c8c8c', label: 'No clear change', tag: 'default' },
  not_enough_data: { color: '#bfbfbf', label: 'Not enough data', tag: 'default' },
};

// ----------------------------------------------------------------- formats

export function formatValue(unit: ExpV2MetricUnit, v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  switch (unit) {
    case 'share':
      return `${(v * 100).toFixed(1)}%`;
    case 'matches':
      return v.toFixed(2);
    case 'minutes':
      return `${v.toFixed(1)} min`;
    case 'inr':
      return `₹${v.toFixed(2)}`;
  }
}

const signed = (s: string, v: number) => (v > 0 ? `+${s}` : s);

export const formatSignedPercent = (v: number | null): string =>
  v === null || !Number.isFinite(v) ? '—' : signed(`${(v * 100).toFixed(1)}%`, v);

/** A change in the metric's own unit: points for a rate. */
export function formatAbsoluteChange(unit: ExpV2MetricUnit, v: number | null): string {
  if (v === null || !Number.isFinite(v)) return '—';
  if (unit === 'share') return signed(`${(v * 100).toFixed(1)} pts`, v);
  return signed(formatValue(unit, v), v);
}

/** Interval level shown for a per-comparison alpha: 95 for 0.05, 97.5 with two treatments. */
export const intervalLevel = (alpha: number): string => `${Math.round((1 - alpha) * 1000) / 10}%`;

export function formatP(p: number | null): string {
  if (p === null || !Number.isFinite(p)) return '—';
  return p < 0.001 ? '<0.001' : p.toFixed(3);
}

export const formatCount = (n: number): string => n.toLocaleString('en-IN');

/**
 * The change shown in a scorecard cell: relative to control when control is
 * not 0, else in the metric's unit; `detail` is the other form.
 */
export function describeChange(
  m: Pick<ExpV2MetricResult, 'unit'>,
  c: ExpV2Comparison
): { main: string; interval: string; detail: string } {
  if (c.relLift !== null) {
    return {
      main: formatSignedPercent(c.relLift),
      interval: `${formatSignedPercent(c.relCiLow)} to ${formatSignedPercent(c.relCiHigh)}`,
      detail: formatAbsoluteChange(m.unit, c.delta),
    };
  }
  return {
    main: formatAbsoluteChange(m.unit, c.delta),
    interval: `${formatAbsoluteChange(m.unit, c.absCiLow)} to ${formatAbsoluteChange(m.unit, c.absCiHigh)}`,
    detail: '',
  };
}

/** Interval ends used for the bar: relative when available, else absolute. */
export function intervalOf(c: ExpV2Comparison): { low: number; mid: number; high: number } | null {
  if (c.relLift !== null && c.relCiLow !== null && c.relCiHigh !== null) {
    return { low: c.relCiLow, mid: c.relLift, high: c.relCiHigh };
  }
  if (c.delta !== null && c.absCiLow !== null && c.absCiHigh !== null) {
    return { low: c.absCiLow, mid: c.delta, high: c.absCiHigh };
  }
  return null;
}

// ------------------------------------------------------------------- dates

/** When the test first started serving: the oldest start in the state log, else the stable phase. */
export function startedAt(
  t: Pick<ExpV2Detail, 'stateLog' | 'stablePhaseFrom' | 'startsAt'>
): string | null {
  const starts = (t.stateLog ?? [])
    // A start of a scheduled test logs status scheduled; serving begins at the closer's start to running.
    .filter((r) => r.action === 'start' && (r.newValue?.status ?? 'running') === 'running')
    .map((r) => r.at)
    .sort();
  return starts[0] ?? t.stablePhaseFrom ?? t.startsAt ?? null;
}

export const daysSince = (iso: string | null | undefined, now: Date): number | null =>
  iso ? Math.max(0, (now.getTime() - new Date(iso).getTime()) / DAY_MS) : null;

// ----------------------------------------------------------- health checks

export type CheckStatus = 'pass' | 'warn' | 'fail' | 'pending';

export interface HealthCheck {
  key: string;
  label: string;
  status: CheckStatus;
  detail: string;
}

export interface ResultsInput {
  test: ExpV2Detail;
  counts: ExpV2Counts | null;
  results: ExpV2Results | null;
  timeline?: ExpV2Timeline | null;
  now: Date;
}

const pct = (a: number, b: number) => (b === 0 ? 0 : Math.round((a / b) * 1000) / 10);

export function healthChecks({
  test,
  counts,
  results,
  timeline,
  now,
}: ResultsInput): HealthCheck[] {
  const checks: HealthCheck[] = [];
  const assigned = counts?.variants.reduce((n, v) => n + v.assigned, 0) ?? 0;

  // 1. Sample ratio mismatch.
  if (!counts || assigned === 0) {
    checks.push({
      key: 'srm',
      label: 'Split matches the plan',
      status: 'pending',
      detail: 'No players yet.',
    });
  } else if (counts.srm.flagged) {
    checks.push({
      key: 'srm',
      label: 'Split matches the plan',
      status: 'fail',
      detail: `Players per variant differ from the planned weights (p=${counts.srm.pValue.toPrecision(3)}). Assignment is broken; do not trust the results.`,
    });
  } else {
    checks.push({
      key: 'srm',
      label: 'Split matches the plan',
      status: 'pass',
      detail: `No sample ratio mismatch (p=${counts.srm.pValue.toPrecision(3)}).`,
    });
  }

  // 2. Exposure logging: every arm, control included, must log exposures.
  if (counts && assigned > 0) {
    const rates = counts.variants
      .filter((v) => v.assigned > 0)
      .map((v) => ({ variant: v.variant, rate: pct(v.exposed, v.assigned) }));
    const control = rates.find((r) => r.variant === 'control');
    const anyExposed = rates.some((r) => r.rate > 0);
    const spread = Math.max(...rates.map((r) => r.rate)) - Math.min(...rates.map((r) => r.rate));
    const list = rates.map((r) => `${r.variant} ${r.rate}%`).join(', ');
    if (!anyExposed) {
      checks.push({
        key: 'exposure',
        label: 'Exposures logged',
        status: 'warn',
        detail: 'No exposures yet. Read all assigned players; "Exposed only" has nobody in it.',
      });
    } else if (control && control.rate === 0) {
      checks.push({
        key: 'exposure',
        label: 'Exposures logged',
        status: 'fail',
        detail: `Control logs no exposures (${list}). Exposed-only reads compare unlike groups.`,
      });
    } else if (spread > 10) {
      checks.push({
        key: 'exposure',
        label: 'Exposures logged',
        status: 'warn',
        detail: `Exposure rate differs by arm (${list}). Read all assigned players (intent to treat).`,
      });
    } else {
      checks.push({
        key: 'exposure',
        label: 'Exposures logged',
        status: 'pass',
        detail: `Exposed: ${list}.`,
      });
    }
  }

  // 3. Players seen in more than one variant.
  if (results) {
    const n = results.multiVariantPlayers;
    checks.push({
      key: 'multi',
      label: 'Each player in one variant',
      status: n === 0 ? 'pass' : 'warn',
      detail:
        n === 0
          ? 'No player was seen in two variants.'
          : `${formatCount(n)} players were seen in two variants and are left out of the results.`,
    });
  }

  // 4. Sample size for the pre-registered effect.
  const s = results?.sampleSize;
  if (results && !s) {
    checks.push({
      key: 'sample',
      label: 'Enough players',
      status: 'pending',
      detail: isCatalogueMetric(test.preRegistration?.primaryMetric)
        ? 'Not enough data to estimate yet.'
        : 'The primary metric is not a standard metric, so no sample size can be estimated.',
    });
  } else if (s) {
    const metric = metricLabel(s.metric);
    if (s.perArmNeeded === null) {
      checks.push({
        key: 'sample',
        label: 'Enough players',
        status: 'pending',
        detail: `Not enough control data yet to size ${metric}.`,
      });
    } else if (s.perArmHave >= s.perArmNeeded) {
      checks.push({
        key: 'sample',
        label: 'Enough players',
        status: 'pass',
        detail: `${formatCount(s.perArmHave)} per variant counted for ${metric}; ${formatCount(s.perArmNeeded)} needed to see the planned effect.`,
      });
    } else {
      const eta = daysToSample(s.perArmNeeded - s.perArmHave, test, timeline, now);
      checks.push({
        key: 'sample',
        label: 'Enough players',
        status: 'warn',
        detail:
          `${formatCount(s.perArmHave)} of ${formatCount(s.perArmNeeded)} per variant for ${metric} ` +
          `(${pct(s.perArmHave, s.perArmNeeded)}%).` +
          (eta === null
            ? ''
            : ` About ${eta} more day${eta === 1 ? '' : 's'} at the current intake, plus the metric's window.`),
      });
    }
  }

  // 5. Run time: at least a full week from the stable phase.
  const from = test.stablePhaseFrom ?? null;
  if (!from) {
    if (test.status === 'running' || test.status === 'paused') {
      checks.push({
        key: 'duration',
        label: 'Ran a full week',
        status: 'warn',
        detail:
          'Still in the safety phase: open the full range ("Go to target") to start the main read.',
      });
    }
  } else {
    const days = daysSince(from, now) ?? 0;
    checks.push({
      key: 'duration',
      label: 'Ran a full week',
      status: days >= MIN_RUN_DAYS ? 'pass' : 'warn',
      detail:
        days >= MIN_RUN_DAYS
          ? `${days.toFixed(1)} days since the full range opened.`
          : `${days.toFixed(1)} of ${MIN_RUN_DAYS} days since the full range opened. Early reads move; weekdays and weekends differ.`,
    });
  }
  return checks;
}

/**
 * Days of intake still needed for the smallest arm, from the average of the
 * last 3 complete days after the stable phase opened, at that arm's weight share.
 */
export function daysToSample(
  missingPerArm: number,
  test: Pick<ExpV2Test, 'variants' | 'stablePhaseFrom'>,
  timeline: ExpV2Timeline | null | undefined,
  now: Date
): number | null {
  if (!timeline || missingPerArm <= 0) return null;
  const today = istDay(now);
  const from = test.stablePhaseFrom ? istDay(new Date(test.stablePhaseFrom)) : null;
  const perDay = new Map<string, number>();
  for (const r of timeline.rows) {
    if (r.day >= today || (from && r.day <= from)) continue; // complete days after the opening day only
    perDay.set(r.day, (perDay.get(r.day) ?? 0) + r.assigned);
  }
  const days = [...perDay.keys()].sort().slice(-3);
  const weights = test.variants.filter((v) => v.state === 'active').map((v) => v.weight);
  const totalWeight = weights.reduce((n, w) => n + w, 0);
  if (days.length === 0 || totalWeight <= 0) return null;
  // The smallest arm fills at its own weight share, and it is the one counted.
  const share = Math.min(...weights) / totalWeight;
  const rate = (days.reduce((n, d) => n + (perDay.get(d) ?? 0), 0) / days.length) * share;
  return rate > 0 ? Math.ceil(missingPerArm / rate) : null;
}

/** YYYY-MM-DD in IST. */
export const istDay = (d: Date): string =>
  d.toLocaleString('sv-SE', { timeZone: 'Asia/Kolkata' }).slice(0, 10);

// ---------------------------------------------------------- recommendation

export interface Recommendation {
  type: 'success' | 'info' | 'warning' | 'error';
  title: string;
  detail: string;
}

export function recommendation(input: ResultsInput): Recommendation | null {
  const { test, counts, results, now } = input;
  if (!results) return null;
  if (counts?.srm.flagged) {
    return {
      type: 'error',
      title: 'Do not read these results: the split is off',
      detail:
        'Players per variant do not match the planned weights. Check targeting, overrides and the client before reading any metric.',
    };
  }
  const exposure = healthChecks(input).find((c) => c.key === 'exposure');
  if (results.population === 'exposed' && exposure && exposure.status !== 'pass') {
    return {
      type: 'warning',
      title: 'Read all assigned players instead',
      detail: `The exposed-only read is biased here: ${exposure.detail}`,
    };
  }
  const harmed = results.metrics
    .filter((m) => m.role === 'guardrail')
    .flatMap((m) =>
      m.comparisons.filter((c) => c.verdict === 'worse').map((c) => `${m.label} (${c.variant})`)
    );
  if (harmed.length > 0) {
    return {
      type: 'warning',
      title: 'A guardrail got worse',
      detail: `${harmed.join(', ')} is significantly worse than control. Weigh this before shipping.`,
    };
  }
  const primary = results.metrics.find((m) => m.role === 'primary');
  if (!primary) {
    const text = test.preRegistration?.primaryMetric;
    return {
      type: 'info',
      title: 'No standard primary metric',
      detail: text
        ? `The primary metric "${text}" is not one this page scores; read it with the custom analysis. The standard metrics below are secondary reads.`
        : 'This test has no primary metric. The standard metrics below are secondary reads.',
    };
  }
  const winners = primary.comparisons.filter((c) => c.verdict === 'better');
  const losers = primary.comparisons.filter((c) => c.verdict === 'worse');
  const days = daysSince(test.stablePhaseFrom, now);
  const s = results.sampleSize;
  const lead = (c: ExpV2Comparison) => `${c.variant} ${describeChange(primary, c).main}`;
  const unreadable = results.metrics
    .filter(
      (m) => m.role === 'guardrail' && m.comparisons.some((c) => c.verdict === 'not_enough_data')
    )
    .map((m) => m.label);

  // Every reason not to call the test yet; a winner is named only when there is none.
  const reasons: string[] = [];
  if (!counts) reasons.push('the split check has not loaded');
  if (days === null) reasons.push('the full range is not open yet');
  else if (days < MIN_RUN_DAYS) reasons.push(`${days.toFixed(1)} of ${MIN_RUN_DAYS} days run`);
  if (!s || s.perArmNeeded === null) reasons.push('the sample size cannot be estimated yet');
  else if (s.perArmHave < s.perArmNeeded) {
    reasons.push(
      `${formatCount(s.perArmHave)} of ${formatCount(s.perArmNeeded)} players per variant`
    );
  }
  if (primary.comparisons.some((c) => c.verdict === 'not_enough_data')) {
    reasons.push(`${primary.label} is not readable yet`);
  }
  if (unreadable.length > 0) reasons.push(`guardrails not readable yet: ${unreadable.join(', ')}`);

  if (reasons.length > 0) {
    const so = winners.length
      ? ` So far ${winners.map(lead).join(', ')} on ${primary.label}.`
      : losers.length
        ? ` So far ${losers.map(lead).join(', ')} on ${primary.label}.`
        : '';
    return {
      type: 'info',
      title: 'Keep running',
      detail: `Not ready to call: ${reasons.join('; ')}.${so}`,
    };
  }
  if (winners.length > 0) {
    return {
      type: 'success',
      title: `Ready to decide: ${winners.map((c) => c.variant).join(', ')} wins`,
      detail: `${winners.map(lead).join(', ')} on ${primary.label}; every guardrail was readable and none got worse.`,
    };
  }
  if (losers.length > 0) {
    return {
      type: 'warning',
      title: `${losers.map((c) => c.variant).join(', ')} is worse than control`,
      detail: `${losers.map(lead).join(', ')} on ${primary.label}.`,
    };
  }
  const planned = test.preRegistration?.plannedEndDate;
  const past = planned ? istDay(now) > planned : false;
  return {
    type: 'info',
    title: 'No clear difference yet',
    detail: past
      ? `The planned end (${planned}) has passed with no clear change on ${primary.label}. End it and record kill or iterate.`
      : `No variant differs from control on ${primary.label} at the planned sample. Run to the planned end${planned ? ` (${planned})` : ''}.`,
  };
}

// --------------------------------------------------------------- timeline

export interface SeriesPoint {
  day: string;
  variant: string;
  value: number;
}

/** Cumulative players per variant for every day from the first row to the last (gaps filled). */
export function cumulativeSeries(
  timeline: ExpV2Timeline,
  order: string[],
  field: 'assigned' | 'exposed'
): SeriesPoint[] {
  if (timeline.rows.length === 0) return [];
  const days = timeline.rows.map((r) => r.day).sort();
  const all: string[] = [];
  for (let d = days[0]; d <= days[days.length - 1]; d = nextDay(d)) all.push(d);
  const variants = [
    ...order,
    ...[...new Set(timeline.rows.map((r) => r.variant))].filter((v) => !order.includes(v)),
  ].filter((v) => timeline.rows.some((r) => r.variant === v));
  const byKey = new Map(timeline.rows.map((r) => [`${r.day}|${r.variant}`, r[field]]));
  const out: SeriesPoint[] = [];
  for (const v of variants) {
    let total = 0;
    for (const d of all) {
      total += byKey.get(`${d}|${v}`) ?? 0;
      out.push({ day: d, variant: v, value: total });
    }
  }
  return out;
}

function nextDay(d: string): string {
  const t = new Date(`${d}T00:00:00Z`);
  t.setUTCDate(t.getUTCDate() + 1);
  return t.toISOString().slice(0, 10);
}
